import 'moment-timezone';

import {
    ChangeDetectionStrategy,
    Component,
    OnDestroy,
    OnInit,
} from '@angular/core';
import { Router } from '@angular/router';
import {
    AppStateFacade,
    Setting,
} from '@app-state';
import { ChartTooltipTrigger } from '@components/ui-chart/base-chart.types';
import { UiModalComponent } from '@components/ui-modal/ui-modal.component';
import {
    Interval,
    ITransaction,
} from '@models';
import { TranslateService } from '@ngx-translate/core';
import {
    DEFAULT_TRANSACTION_INTERVALS,
    IAggregatedTransactions,
    intervalToUnit,
    unitToFormat,
} from '@pages/shared/chart.config';
import {
    ChartColorService,
    ChartService,
    ResourceMapperService,
    StorageService,
    TransactionService,
} from '@services';
import { unsubscribe } from '@utils/object.utils';

import * as moment from 'moment';
import {
    BehaviorSubject,
    Subscription,
} from 'rxjs';

interface ITransactionsLineChart {
    loading$: BehaviorSubject<boolean>;
    hasData: boolean;
    data: number[] | null;
    legend: string[] | null;
    labels: string[] | null;
    colors: string[];
}

@Component({
    styleUrls: ['transaction-chart.modal.scss'],
    templateUrl: './transaction-chart.modal.html',
    providers: [TransactionService, ChartService],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TransactionChartModalComponent
    extends UiModalComponent<ITransaction>
    implements OnInit, OnDestroy {

    public ChartTooltipTrigger = ChartTooltipTrigger;
    public defaultTransactionIntervals = DEFAULT_TRANSACTION_INTERVALS;

    public chart: ITransactionsLineChart = {
        loading$: new BehaviorSubject<boolean>(true),
        hasData: false,
        data: null,
        legend: null,
        labels: null,
        colors: [],
    };

    public transactionInterval!: Interval;
    public intervalLabel?: string;
    public averageTime: any;

    public get labelFormat(): string {
        return this._labelFormat;
    }

    public set labelFormat(value: string) {
        this._labelFormat = unitToFormat(value);
    }

    private _labelFormat = '';
    private _translationSub?: Subscription;
    private _statSub?: Subscription;

    private _appState = this._injector.get<AppStateFacade>(AppStateFacade);
    private _chartService = this._injector.get<ChartService>(ChartService);
    private _transactionService = this._injector.get<TransactionService>(TransactionService);
    private _translateService = this._injector.get<TranslateService>(TranslateService);
    private _storageService = this._injector.get<StorageService>(StorageService);
    private _router = this._injector.get<Router>(Router);
    private _chartColorService = this._injector.get<ChartColorService>(ChartColorService);

    private _intervalCacheKey!: string;

    ngOnInit() {
        this.chart.colors = this._chartColorService.transactions;

        const userId = this._appState.user.value!.id;
        this._intervalCacheKey = `${this._router.url} chart - ${userId} - Transactions`;
        this.transactionInterval = this._storageService.get(this._intervalCacheKey, Interval.Hour);

        this._loadTransactionStats(this.transactionInterval, this.context.data.id);
    }

    ngOnDestroy() {
        unsubscribe(
            this._translationSub,
            this._statSub,
        );
    }

    public onIntervalChange(interval?: Interval) {
        // if not filter is saved in localStorage, use the default
        this.transactionInterval = interval != null ? interval : this.transactionInterval;
        this._loadTransactionStats(this.transactionInterval, this.context.data.id);
        this._storageService.set(this._intervalCacheKey, this.transactionInterval);
    }

    // tslint:disable-next-line:member-ordering
    public onRefreshClick() {
        this._loadTransactionStats(this.transactionInterval, this.context.data.id);
    }

    private _loadTransactionStats(interval: Interval, queueId: number) {
        this.labelFormat = intervalToUnit(interval);
        this.chart.hasData = false;
        this.chart.loading$.next(true);
        this.intervalLabel = this._chartService.getTransactionsLabelForInterval(this.transactionInterval);
        this._statSub = this._transactionService
            .getTransactionStats(interval, queueId)
            .subscribe(res => {
                this._generateTransactionChart(res);
            });
    }

    private _generateTransactionChart(result: IAggregatedTransactions) {
        this.averageTime = result.averageTime;

        // Chart lines are rendered in the order that they are in this array. Reverse them so the totals line is rendered
        // first, not last (i.e. so it no longer covers the other lines).
        result.chartData.reverse();

        const transactionTypes = result.chartData
            .map((entry: { label: string | number; }) => ResourceMapperService.transactionStatus[entry.label]);

        this.chart.legend = [];
        this.chart.data = [];

        if (this._translationSub) {
            this._translationSub.unsubscribe();
        }

        result.chartData.forEach((chartData: { data: number; }) => {
            this.chart.data!.push(chartData.data);
        });

        this._translationSub = this._translateService.stream(transactionTypes)
            .subscribe(translations => {
                this.chart.data!.forEach((_entry, idx) => {
                    this.chart.legend![idx] = translations[transactionTypes[idx]];
                });
            });

        this.chart.labels = result
            .chartLabels
            .map((label: moment.MomentInput) => moment(label)
                .tz(this._appState.settings.get(Setting.timezone)!)
                .format(this.labelFormat));

        this.chart.hasData = result.hasData;
        this.chart.loading$.next(false);
    }
}
